import axios, { AxiosResponse, AxiosRequestConfig } from 'axios';
import { toast } from 'react-hot-toast';
import config from '../Config/config.json'
import { handleAuthFailure } from './util';

const baseURL = config.apiBaseUrl;
const httpClient = axios.create({
  baseURL,
  withCredentials:true,
});

// Define public routes to skip global auth handling
const publicRoutes = ['/login'];

const isPublicRoute = () => {
  return publicRoutes.includes(window.location.pathname);
};

// request with token
httpClient.interceptors.request.use((config) => {
  // Ensure cookies are sent with requests
  config.withCredentials = true;
  return config;
});


httpClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Don't retry refresh token
    if (originalRequest.url.includes('/auth/refresh-token')) {
      if (window.location.pathname !== '/login') {
        handleAuthFailure();
      }
      return Promise.reject(error);
    }

    // 🔁 Try refresh on 401 once
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      try {
        await httpClient.get('/auth/refresh-token');
        return httpClient(originalRequest);
      } catch (refreshError) {
        if (!isPublicRoute()) {
        handleAuthFailure();
      }
        return Promise.reject(refreshError);
      }
    }

    if (error.response?.status === 429) {
      toast.error(error.response?.data.message || "You're sending too many requests. Try again later.");
      return Promise.reject(new Error('GlobalHandledError'));
      }

    // 🟥 500 Server errors
    if (error.response?.status === 500) {
      toast.error("Server error. Please try again later.");
      return Promise.reject(new Error('GlobalHandledError'));
    }

    // 🌐 some other issue
    if (!error.response) {
      // toast.error("Something went wrong. Please try again later.");
      return Promise.reject(new Error('GlobalHandledError'));
    }

    return Promise.reject(error);
  }
);

const get = async function <T>(url: string, config?: AxiosRequestConfig): Promise<T> {
  try {
    const response: AxiosResponse<T> = await httpClient.get<T>(url, config);
    return response.data;
  } catch (error) {
    console.error('Error fetching data:', error);
    throw error;
  }
};

const post = async function <T>(url: string, data: any, config?: AxiosRequestConfig): Promise<T> {
  try {
    const response: AxiosResponse<T> = await httpClient.post(url, data, config);
    return response.data;
  } catch (error) {
    console.error('Error posting data:', error);
    throw error;
  }
};

const del = async function <T>(url: string, config?: AxiosRequestConfig): Promise<T> {
  try {
    const response: AxiosResponse<T> = await httpClient.delete(url, config);
    return response.data;
  } catch (error) {
    console.error('Error deleting data:', error);
    throw error;
  }
};
const put = async function <T>(url: string, data: any, config?: AxiosRequestConfig): Promise<T> {
  try {
    const response: AxiosResponse<T> = await httpClient.put<T>(url, data, config);
    return response.data;
  } catch (error) {
    console.error('Error putting data:', error);
    throw error;
  }
};

const patch = async function <T>(url: string, data: any, config?: AxiosRequestConfig): Promise<T> {
  try {
    const response: AxiosResponse<T> = await httpClient.patch<T>(url, data, config);
    return response.data;
  } catch (error) {
    console.error('Error patching data:', error);
    throw error;
  }
};

// Dedicated function for posting FormData
const postFormData = async function <T>(
  url: string,
  formData: FormData, // Explicitly expect FormData
  config?: AxiosRequestConfig
): Promise<T> {
  try {
    const response: AxiosResponse<T> = await httpClient.post(url, formData, {
      headers: {
        "Content-Type": "multipart/form-data", // Set FormData-specific header
        ...config?.headers, // Merge custom headers if provided
      },
      ...config, // Spread other configurations
    });

    return response.data;
  } catch (error: any) {
    console.error("Error posting FormData:", error);
    return error.response?.data || error.message;
  }
};
const putFormData = async function <T>(
  url: string,
  formData: FormData, // Explicitly expect FormData
  config?: AxiosRequestConfig
): Promise<T> {
  try {
    const response: AxiosResponse<T> = await httpClient.put(url, formData, {
      headers: {
        "Content-Type": "multipart/form-data", // Set FormData-specific header
        ...config?.headers, // Merge custom headers if provided
      },
      ...config, // Spread other configurations
    });

    return response.data;
  } catch (error: any) {
    console.error("Error updating FormData:", error);
    return error.response?.data || error.message;
  }
};

const downloadFile = async (url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<Blob>> => {
  try {
    const response = await httpClient.get<Blob>(url, {
      responseType: 'blob',
      ...config,
    });
    return response;
  } catch (error) {
    console.error('File download error:', error);
    throw error;
  }
};


const http = {
  get,
  post,
  del,
  put,
  patch,
  postFormData,
  putFormData,
  downloadFile
};

export default http;
